/**
 * Video Processing Service
 *
 * Dynamic video processing with filesystem cache.
 * Separated from StorageService to keep concerns clean:
 * - StorageService: file CRUD, dedup, scopes
 * - VideoProcessingService: ffmpeg transforms, cache management
 */

import { join } from 'node:path'
import { existsSync, mkdirSync } from 'node:fs'
import { readFile, writeFile, rm, mkdir, stat } from 'node:fs/promises'
import { createHash } from 'node:crypto'
import type { Logger } from 'pino'
import type { VideoConfig } from './storage.config.js'
import type { VideoMetadata } from './storage.types.js'
import { detectFfmpeg, runFfprobe, runFfmpeg, type FfmpegEncoders } from './ffmpeg.utils.js'

export class VideoEncoderUnavailableError extends Error {
  readonly statusCode = 400
  readonly code = 'VIDEO_ENCODER_UNAVAILABLE'
  constructor(encoder: string, feature: string) {
    super(`${feature} unavailable: ffmpeg not built with ${encoder}`)
    this.name = 'VideoEncoderUnavailableError'
  }
}

// ============================================================================
// TYPES
// ============================================================================

export interface ThumbnailOptions {
  /** Timestamp in seconds to extract frame (default: 0) */
  timestamp?: number
  /** Width in pixels */
  w?: number
  /** Height in pixels */
  h?: number
  /** JPEG quality 1-100 (default: 80) */
  q?: number
}

export interface PreviewOptions {
  /** Start time in seconds (default: 0) */
  start?: number
  /** Duration in seconds (default: 3, max: config.maxPreviewDuration) */
  duration?: number
  /** Width in pixels (default: 320) */
  w?: number
  /** Output format (default: gif) */
  format?: 'gif' | 'webp'
}

export interface TranscodeOptions {
  /** Target container format */
  format: 'mp4' | 'webm'
  /** Quality preset (default: medium) */
  quality?: 'high' | 'medium' | 'low'
  /** Max width (aspect ratio preserved) */
  maxWidth?: number
  /** Max height (aspect ratio preserved) */
  maxHeight?: number
}

export interface VideoResult {
  buffer: Buffer
  mimetype: string
  format: string
  cacheHit: boolean
}

// ============================================================================
// SINGLETON
// ============================================================================

let instance: VideoProcessingService | null = null

/**
 * Gets the video processing service singleton.
 */
export function getVideoProcessingService(): VideoProcessingService {
  if (!instance) {
    throw new Error('VideoProcessingService not initialized. Call initVideoProcessingService() first.')
  }
  return instance
}

/**
 * Initializes the video processing service singleton.
 */
export function initVideoProcessingService(config: VideoConfig, logger: Logger): VideoProcessingService {
  instance = new VideoProcessingService(config, logger)
  return instance
}

// ============================================================================
// HELPERS
// ============================================================================

/** Known video MIME types for client-side filtering */
export const VIDEO_MIMETYPES_LIST = [
  'video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo',
  'video/x-matroska', 'video/ogg', 'video/x-ms-wmv', 'video/3gpp',
  'video/mpeg', 'video/x-flv'
] as const

const VIDEO_MIMETYPES = new Set<string>(VIDEO_MIMETYPES_LIST)

/**
 * Checks if a mimetype is a video type.
 */
export function isVideoMimetype(mimetype: string): boolean {
  return VIDEO_MIMETYPES.has(mimetype) || mimetype.startsWith('video/')
}

const FORMAT_TO_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  webm: 'video/webm'
}

// ============================================================================
// SERVICE
// ============================================================================

export class VideoProcessingService {
  private config: VideoConfig
  private logger: Logger
  private cacheReady = false
  private available: boolean | null = null
  private encoders: FfmpegEncoders | null = null
  private pendingKeys = new Set<string>()

  constructor(config: VideoConfig, logger: Logger) {
    this.config = config
    this.logger = logger.child({ service: 'video-processing' })
  }

  /**
   * Checks if ffmpeg/ffprobe are available (cached).
   */
  async isAvailable(): Promise<boolean> {
    if (this.available !== null) return this.available
    const result = await detectFfmpeg(this.logger)
    this.available = result.ffmpeg && result.ffprobe
    this.encoders = result.encoders
    return this.available
  }

  /**
   * Returns the encoders compiled into the system ffmpeg.
   * Triggers detection if not yet cached.
   */
  async getEncoders(): Promise<FfmpegEncoders> {
    if (!this.encoders) await this.isAvailable()
    return this.encoders ?? { libwebp: false, libx264: false, libvpx: false, libopus: false, aac: false }
  }

  private async assertEncoder(encoder: keyof FfmpegEncoders, feature: string): Promise<void> {
    const enc = await this.getEncoders()
    if (!enc[encoder]) throw new VideoEncoderUnavailableError(encoder, feature)
  }

  /**
   * Extracts video metadata via ffprobe.
   */
  async getMetadata(inputPath: string): Promise<VideoMetadata> {
    const probe = await runFfprobe(inputPath)
    const streams = probe['streams'] as Array<Record<string, unknown>> | undefined
    const format = probe['format'] as Record<string, unknown> | undefined

    const videoStream = streams?.find(s => s['codec_type'] === 'video')
    const audioStream = streams?.find(s => s['codec_type'] === 'audio')

    const rFrameRate = videoStream?.['r_frame_rate'] as string | undefined
    let fps = 0
    if (rFrameRate) {
      const [num, den] = rFrameRate.split('/')
      fps = den ? Math.round(parseInt(num!, 10) / parseInt(den, 10)) : parseFloat(num!)
    }

    return {
      duration: parseFloat(format?.['duration'] as string || '0'),
      width: (videoStream?.['width'] as number) || 0,
      height: (videoStream?.['height'] as number) || 0,
      codec: (videoStream?.['codec_name'] as string) || 'unknown',
      bitrate: parseInt(format?.['bit_rate'] as string || '0', 10),
      fps,
      audioCodec: audioStream?.['codec_name'] as string | undefined,
      format: (format?.['format_name'] as string)?.split(',')[0] || 'unknown'
    }
  }

  /**
   * Validates and normalizes thumbnail options.
   * Returns null if no `frame` param is present (no thumbnail requested).
   */
  parseThumbnailOptions(raw: Record<string, string | undefined>): ThumbnailOptions | null {
    if (raw['frame'] === undefined) return null

    const tRaw = raw['t'] ?? raw['timestamp']
    const wRaw = raw['w'] ?? raw['width']
    const hRaw = raw['h'] ?? raw['height']
    const qRaw = raw['q'] ?? raw['quality']

    const timestamp = tRaw ? parseFloat(tRaw) || 0 : 0
    const w = wRaw ? Math.min(Math.max(1, parseInt(wRaw, 10) || 0), this.config.maxWidth) : undefined
    const h = hRaw ? Math.min(Math.max(1, parseInt(hRaw, 10) || 0), this.config.maxHeight) : undefined
    const q = qRaw ? Math.min(100, Math.max(1, parseInt(qRaw, 10) || this.config.defaultQuality)) : this.config.defaultQuality

    return { timestamp, w, h, q }
  }

  /**
   * Validates and normalizes preview options.
   * Returns null if no `preview` param is present (no preview requested).
   */
  parsePreviewOptions(raw: Record<string, string | undefined>): PreviewOptions | null {
    if (raw['preview'] === undefined) return null

    const startRaw = raw['s'] ?? raw['start']
    const durationRaw = raw['d'] ?? raw['duration']
    const wRaw = raw['w'] ?? raw['width']
    const fmtRaw = raw['f'] ?? raw['format']

    const start = startRaw ? parseFloat(startRaw) || 0 : 0
    const duration = durationRaw
      ? Math.min(parseFloat(durationRaw) || 3, this.config.maxPreviewDuration)
      : 3
    const w = wRaw ? Math.min(Math.max(1, parseInt(wRaw, 10) || 320), this.config.maxWidth) : 320
    const format = fmtRaw === 'webp' ? 'webp' : 'gif'

    return { start, duration, w, format }
  }

  /**
   * Extracts a thumbnail frame from a video.
   */
  async extractThumbnail(inputPath: string, fileId: string, opts: ThumbnailOptions): Promise<VideoResult> {
    const cacheKey = this.buildCacheKey(fileId, 'thumb', opts as unknown as Record<string, unknown>)
    const cachePath = join(this.config.cachePath, fileId, `${cacheKey}.jpg`)

    // Check cache
    if (existsSync(cachePath)) {
      const cached = await readFile(cachePath)
      return { buffer: cached, mimetype: 'image/jpeg', format: 'jpg', cacheHit: true }
    }

    // Dedup concurrent requests
    if (this.pendingKeys.has(cacheKey)) {
      await this.waitForCache(cachePath, 10000)
      if (existsSync(cachePath)) {
        const cached = await readFile(cachePath)
        return { buffer: cached, mimetype: 'image/jpeg', format: 'jpg', cacheHit: true }
      }
    }

    this.pendingKeys.add(cacheKey)
    try {
      await this.ensureCacheDir()
      const tmpOutput = join(this.config.cachePath, `tmp_${cacheKey}.jpg`)
      const qv = String(Math.round((100 - (opts.q || 80)) / 100 * 30 + 1))

      const args: string[] = []
      args.push('-ss', String(opts.timestamp || 0))
      args.push('-i', inputPath)
      args.push('-vframes', '1')

      const scale = this.buildScaleFilter(opts.w, opts.h)
      if (scale) args.push('-vf', scale)

      args.push('-q:v', qv, '-update', '1', '-f', 'image2', tmpOutput)

      await runFfmpeg(args)

      const buffer = await readFile(tmpOutput)

      // Write to cache (fire-and-forget)
      this.writeCache(cachePath, buffer).catch(err => {
        this.logger.warn({ err, fileId, cacheKey }, 'Failed to write thumbnail cache')
      })

      // Clean up tmp
      rm(tmpOutput, { force: true }).catch(() => {})

      this.logger.debug({ fileId, timestamp: opts.timestamp, size: buffer.length }, 'Thumbnail extracted')

      return { buffer, mimetype: 'image/jpeg', format: 'jpg', cacheHit: false }
    } finally {
      this.pendingKeys.delete(cacheKey)
    }
  }

  /**
   * Generates an animated preview (GIF or WebP).
   */
  async generatePreview(inputPath: string, fileId: string, opts: PreviewOptions): Promise<VideoResult> {
    const format = opts.format || 'gif'
    if (format === 'webp') await this.assertEncoder('libwebp', 'webp preview')
    const cacheKey = this.buildCacheKey(fileId, 'preview', opts as unknown as Record<string, unknown>)
    const cachePath = join(this.config.cachePath, fileId, `${cacheKey}.${format}`)

    // Check cache
    if (existsSync(cachePath)) {
      const cached = await readFile(cachePath)
      return {
        buffer: cached,
        mimetype: FORMAT_TO_MIME[format] || 'image/gif',
        format,
        cacheHit: true
      }
    }

    // Dedup concurrent requests
    if (this.pendingKeys.has(cacheKey)) {
      await this.waitForCache(cachePath, 30000)
      if (existsSync(cachePath)) {
        const cached = await readFile(cachePath)
        return { buffer: cached, mimetype: FORMAT_TO_MIME[format] || 'image/gif', format, cacheHit: true }
      }
    }

    this.pendingKeys.add(cacheKey)
    try {
      await this.ensureCacheDir()
      const tmpOutput = join(this.config.cachePath, `tmp_${cacheKey}.${format}`)
      const w = opts.w || 320

      const args: string[] = []
      args.push('-ss', String(opts.start || 0))
      args.push('-t', String(opts.duration || 3))
      args.push('-i', inputPath)
      args.push('-vf', `fps=10,scale=${w}:-1:flags=lanczos`)

      if (format === 'webp') {
        args.push('-c:v', 'libwebp')
      }

      args.push('-loop', '0', '-an', tmpOutput)

      await runFfmpeg(args)

      const buffer = await readFile(tmpOutput)

      this.writeCache(cachePath, buffer).catch(err => {
        this.logger.warn({ err, fileId, cacheKey }, 'Failed to write preview cache')
      })

      rm(tmpOutput, { force: true }).catch(() => {})

      this.logger.debug({ fileId, format, duration: opts.duration, size: buffer.length }, 'Preview generated')

      return {
        buffer,
        mimetype: FORMAT_TO_MIME[format] || 'image/gif',
        format,
        cacheHit: false
      }
    } finally {
      this.pendingKeys.delete(cacheKey)
    }
  }

  /**
   * Transcodes a video to a different format.
   * Writes output to the specified path (does NOT use cache).
   */
  async transcode(
    inputPath: string,
    outputPath: string,
    opts: TranscodeOptions
  ): Promise<{ mimetype: string; format: string }> {
    // Check file size against maxTranscodeSize
    const fileStat = await stat(inputPath)
    if (fileStat.size > this.config.maxTranscodeSize) {
      throw new Error(
        `File size ${fileStat.size} exceeds max transcode size ${this.config.maxTranscodeSize}`
      )
    }

    if (opts.format === 'mp4') {
      await this.assertEncoder('libx264', 'mp4 transcode')
      await this.assertEncoder('aac', 'mp4 audio (aac)')
    } else {
      await this.assertEncoder('libvpx', 'webm transcode (libvpx-vp9)')
      await this.assertEncoder('libopus', 'webm audio (libopus)')
    }

    const args: string[] = ['-i', inputPath]

    const scale = this.buildScaleFilter(opts.maxWidth, opts.maxHeight)
    if (scale) args.push('-vf', scale)

    if (opts.format === 'mp4') {
      const crf = opts.quality === 'high' ? '18' : opts.quality === 'low' ? '28' : '23'
      const preset = opts.quality === 'high' ? 'slow' : 'medium'
      args.push('-c:v', 'libx264', '-preset', preset, '-crf', crf)
      args.push('-c:a', 'aac', '-movflags', '+faststart')
    } else {
      // webm
      const crf = opts.quality === 'high' ? '20' : opts.quality === 'low' ? '40' : '30'
      args.push('-c:v', 'libvpx-vp9', '-crf', crf, '-b:v', '0')
      args.push('-c:a', 'libopus')
    }

    args.push(outputPath)

    await runFfmpeg(args)

    this.logger.debug({ inputPath, outputPath, format: opts.format }, 'Video transcoded')

    return {
      mimetype: FORMAT_TO_MIME[opts.format] || 'video/mp4',
      format: opts.format
    }
  }

  /**
   * Invalidates all cached outputs for a file.
   */
  async invalidateCache(fileId: string): Promise<void> {
    const cacheDir = join(this.config.cachePath, fileId)
    if (existsSync(cacheDir)) {
      await rm(cacheDir, { recursive: true, force: true })
      this.logger.debug({ fileId }, 'Video cache invalidated')
    }
  }

  /**
   * Builds ETag for conditional requests.
   */
  buildETag(fileHash: string | undefined, type: string, opts: Record<string, unknown>): string {
    const key = this.buildCacheKey(fileHash || 'unknown', type, opts)
    return `"v-${key}"`
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  /**
   * Builds ffmpeg scale filter preserving aspect ratio.
   */
  private buildScaleFilter(w?: number, h?: number): string | null {
    if (!w && !h) return null
    const sw = w ? String(w) : '-1'
    const sh = h ? String(h) : '-1'
    return `scale=${sw}:${sh}:force_original_aspect_ratio=decrease`
  }

  /**
   * Builds a deterministic cache key from normalized options.
   */
  private buildCacheKey(id: string, type: string, opts: Record<string, unknown>): string {
    const normalized = JSON.stringify({ type, ...opts })
    return createHash('sha256').update(`${id}:${normalized}`).digest('hex').slice(0, 16)
  }

  /**
   * Writes a buffer to cache, creating directories as needed.
   */
  private async writeCache(cachePath: string, buffer: Buffer): Promise<void> {
    await this.ensureCacheDir()
    const dir = join(cachePath, '..')
    if (!existsSync(dir)) {
      await mkdir(dir, { recursive: true })
    }
    await writeFile(cachePath, buffer)
  }

  /**
   * Ensures the base cache directory exists (lazy init).
   */
  private async ensureCacheDir(): Promise<void> {
    if (this.cacheReady) return
    if (!existsSync(this.config.cachePath)) {
      mkdirSync(this.config.cachePath, { recursive: true })
    }
    this.cacheReady = true
  }

  /**
   * Waits for a cache file to appear (concurrent dedup).
   * Polls every 200ms until timeout.
   */
  private waitForCache(cachePath: string, timeout: number): Promise<void> {
    return new Promise(resolve => {
      const start = Date.now()
      const check = () => {
        if (existsSync(cachePath) || Date.now() - start > timeout) {
          resolve()
          return
        }
        setTimeout(check, 200)
      }
      check()
    })
  }
}
